import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Box, Typography, CircularProgress, Alert, Button } from '@mui/material';
import { CheckCircle, ArrowForward } from '@mui/icons-material';
import { useReactiveVar, useQuery, useMutation } from '@apollo/client';
import { userDeatils } from '../../ReactiveVariables/index';
import { toggleSnackbar } from '../../ReactiveVariables/index';
import { GET_ONE_CATERER } from '../../Graphql/queries';
import { REFRESH_STRIPE_ACCOUNT_STATUS } from '../../Graphql/mutations';

export default function StripeOnboardingComplete() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isProcessing, setIsProcessing] = useState(true);
  const [status, setStatus] = useState('processing'); // 'processing', 'success', 'error'
  const user = useReactiveVar(userDeatils);

  // Get caterer ID for refreshing data
  const catererId = user?.data?.login?.catererId || user?.data?.currentUserDetails?.catererId || user?.data?.createCaterer?.catererId;

  // Mutation to refresh the Stripe account status
  const [refreshStripeAccountStatus] = useMutation(REFRESH_STRIPE_ACCOUNT_STATUS, {
    refetchQueries: [
      {
        query: GET_ONE_CATERER,
        variables: { 
          getOneCatererId: catererId, 
          agsType: "id" 
        }
      }
    ],
    onCompleted: (data) => {
      console.log('✅ Refreshed Stripe account status:', data);
    },
    onError: (error) => {
      console.error('❌ Error refreshing Stripe status:', error);
    }
  });

  useEffect(() => {
    const handleOnboardingComplete = async () => {
      try {
        // Get URL parameters from Stripe
        const accountId = searchParams.get('account_id');
        const accountStatus = searchParams.get('account_status');
        
        console.log('Stripe onboarding completed:', { accountId, accountStatus });

        // Refresh the Stripe account status to get the latest verification status
        if (catererId) {
          console.log('🔄 Refreshing Stripe account status for catererId:', catererId);
          await refreshStripeAccountStatus({
            variables: { catererId }
          });
        }

        // Show success message
        setStatus('success');
        setIsProcessing(false);

        // Show success notification
        toggleSnackbar({
          status: true,
          message: 'Stripe onboarding completed successfully!',
          variant: 'success',
        });

        // Wait a moment for user to see the success message
        setTimeout(() => {
          // Redirect based on user type and registration status
          const userType = user?.data?.currentUserDetails?.userType || user?.data?.login?.userType;
          
          if (userType === 'Caterer') {
            // If user is in registration flow, redirect back to registration
            const tabValue = user?.data?.currentUserDetails?.tabValue;
            if (tabValue && parseInt(tabValue) < 5) {
              // Still in registration flow, redirect to registration
              navigate('/register', { 
                state: { 
                  user: 'Caterer',
                  activeStep: 4 // Go back to Stripe Setup step
                } 
              });
            } else {
              // Registration complete, go to caterer dashboard
              navigate('/summary');
            }
          } else {
            // Fallback to home
            navigate('/');
          }
        }, 2000);

      } catch (error) {
        console.error('Error handling onboarding completion:', error);
        setStatus('error');
        setIsProcessing(false);
        
        toggleSnackbar({
          status: true,
          message: 'There was an issue processing your Stripe onboarding. Please try again.',
          variant: 'error',
        });
      }
    };

    handleOnboardingComplete();
  }, [searchParams, navigate, user]);

  if (isProcessing) {
    return (
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center', 
        justifyContent: 'center', 
        minHeight: '100vh',
        gap: 3
      }}>
        <CircularProgress size={60} />
        <Typography variant="h5" color="text.secondary">
          Processing your Stripe onboarding...
        </Typography>
        <Typography variant="body1" color="text.secondary" textAlign="center">
          Please wait while we verify your account setup.
        </Typography>
      </Box>
    );
  }

  if (status === 'success') {
    return (
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center', 
        justifyContent: 'center', 
        minHeight: '100vh',
        gap: 3
      }}>
        <CheckCircle sx={{ fontSize: 80, color: 'success.main' }} />
        <Typography variant="h4" color="success.main">
          Stripe Onboarding Complete!
        </Typography>
        <Typography variant="h6" color="text.secondary" textAlign="center">
          Your Stripe account has been successfully set up.
        </Typography>
        <Typography variant="body1" color="text.secondary" textAlign="center">
          You will be redirected shortly...
        </Typography>
        <Button
          variant="contained"
          color="primary"
          onClick={() => navigate('/register', { 
            state: { 
              user: 'Caterer',
              activeStep: 4
            } 
          })}
          endIcon={<ArrowForward />}
        >
          Continue Registration
        </Button>
      </Box>
    );
  }

  if (status === 'error') {
    return (
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center', 
        justifyContent: 'center', 
        minHeight: '100vh',
        gap: 3
      }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          <Typography variant="h6">
            Onboarding Error
          </Typography>
          <Typography variant="body1">
            There was an issue processing your Stripe onboarding. Please try again or contact support.
          </Typography>
        </Alert>
        <Button
          variant="contained"
          color="primary"
          onClick={() => navigate('/register', { 
            state: { 
              user: 'Caterer',
              activeStep: 4
            } 
          })}
        >
          Return to Registration
        </Button>
      </Box>
    );
  }

  return null;
} 